/**
 * Highlighted text — the in-view gate for `component-highlighted-text.css`.
 *
 * The reference builds the component as a customized built-in element whose constructor
 * registers an in-view callback and, when the word arrives, adds the class every treatment
 * transitions on. This is that, on an ordinary class hook: an `IntersectionObserver` per
 * page rather than a custom element per word, because our theme has no custom-element
 * layer and the behaviour is one line either way.
 *
 * Two details are the reference's and are worth keeping:
 * - it is one-way. The word highlights when it first arrives and stays highlighted; the
 *   observer stops watching it. Re-running the sweep on every scroll past would turn a
 *   piece of typography into a blinking one.
 * - reduced motion is answered in CSS, not here. The stylesheet's reduced-motion block
 *   publishes each treatment's rest state unconditionally, so a reader who suppresses
 *   animation still sees which word is emphasised — suppressing the motion must not
 *   suppress the meaning. That is why this file has no `prefers-reduced-motion` branch:
 *   adding one would be harmless, but it would imply the CSS needed help.
 *
 * The reference additionally awaits its split-words element before highlighting, because
 * its word-splitter sets inline transforms the highlight would otherwise race. Ours splits
 * synchronously at init and only ever touches text nodes — the underline's <svg> and the
 * element's own background are untouched by it — so there is nothing to wait for.
 */
(function () {
  'use strict';

  const ANIMATED = 'is-animated';
  const targets = document.querySelectorAll('.highlighted-text:not([data-highlight-bound])');
  if (targets.length === 0) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add(ANIMATED));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(ANIMATED);
        observer.unobserve(entry.target);
      });
    },
    /* The reference fires at first contact with the viewport, with no margin: a word
       highlighting the instant its line clears the fold is the point of the effect. */
    { threshold: 0.01 }
  );

  targets.forEach((el) => {
    el.dataset.highlightBound = '';
    observer.observe(el);
  });
})();
